import React, { useState } from 'react';

export default function AnalysisResult({ analysis, scenario_type }) {
  if (!analysis) return null;

  if (['hvac_inspection', 'fire_safety'].includes(scenario_type)) {
    return <InspectionResult analysis={analysis} />;
  }
  if (scenario_type === 'exercise_coaching') {
    return <ExerciseResult analysis={analysis} />;
  }
  if (scenario_type === 'health_checkup') {
    return <HealthResult analysis={analysis} />;
  }
  return <LearningResult analysis={analysis} />;
}

function ConfidenceBadge({ confidence }) {
  if (confidence === undefined || confidence === null) return null;
  const val = typeof confidence === 'string' ? parseFloat(confidence) : confidence;
  if (isNaN(val)) return null;

  const pct = Math.round(val * 100);
  let color = '#28a745';
  let label = '높음';
  if (pct < 50) { color = '#dc3545'; label = '낮음'; }
  else if (pct < 75) { color = '#ffc107'; label = '보통'; }

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.3rem',
      padding: '0.25rem 0.7rem',
      background: `${color}18`,
      color: color,
      borderRadius: 'var(--radius-full, 1rem)',
      fontSize: 'var(--font-size-xs, 0.75rem)',
      fontWeight: 600,
      border: `1px solid ${color}33`,
    }}>
      <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: color,
      }} />
      신뢰도 {pct}% ({label})
    </span>
  );
}

function LearningResult({ analysis }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md, 1rem)' }}>
      {/* 신뢰도 배지 */}
      {analysis.confidence && (
        <div style={{
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
        }}>
          <ConfidenceBadge confidence={analysis.confidence} />
        </div>
      )}

      <CollapsibleSection title="문제" icon="📝" content={analysis.problem_text} index={0} />
      <CollapsibleSection title="내 답" icon="✏️" content={analysis.student_answer} bg="#ffe" index={1} />
      <CollapsibleSection title="정답" icon="✓" content={analysis.correct_answer} bg="#efe" index={2} />
      <CollapsibleSection title="해설" icon="💡" content={analysis.explanation} index={3} />
      <CollapsibleSection title="핵심 개념" icon="🎯" content={analysis.key_concept} bg="#eef" index={4} />
      {analysis.difficulty && (
        <div style={{
          fontSize: 'var(--font-size-sm, 0.9rem)',
          color: 'var(--color-text-secondary, #666)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: `${5 * 80}ms`,
        }}>
          <strong>난이도:</strong> {analysis.difficulty}
        </div>
      )}
      {analysis.good_points && Array.isArray(analysis.good_points) && analysis.good_points.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: 'var(--color-success-bg, #d4edda)',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '480ms',
        }}>
          <strong>👍 잘한 점</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {analysis.good_points.map((p, i) => (
              <li key={i} style={{ marginBottom: '0.25rem', fontSize: 'var(--font-size-sm, 0.9rem)', lineHeight: 1.5 }}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {analysis.similar_problems && (
        <CollapsibleSection title="유사 문제" icon="📚" content={analysis.similar_problems} bg="#f5f0ff" index={6} />
      )}
      {analysis.common_mistakes && (
        <CollapsibleSection title="자주 하는 실수" icon="⚠️" content={analysis.common_mistakes} bg="#fff5f0" index={7} />
      )}
    </div>
  );
}

function UrgencyBadge({ urgency }) {
  const config = {
    '즉시': { bg: '#dc354520', color: '#dc3545', border: '#dc354540' },
    '단기': { bg: '#ffc10720', color: '#856404', border: '#ffc10740' },
    '장기': { bg: '#28a74520', color: '#28a745', border: '#28a74540' },
  }[urgency] || { bg: '#eee', color: '#666', border: '#ccc' };

  return (
    <span style={{
      padding: '0.15rem 0.5rem',
      background: config.bg,
      color: config.color,
      border: `1px solid ${config.border}`,
      borderRadius: 'var(--radius-full, 1rem)',
      fontSize: 'var(--font-size-xs, 0.7rem)',
      fontWeight: 600,
      marginLeft: '0.5rem',
    }}>
      {urgency}
    </span>
  );
}

function IssueItem({ issue }) {
  if (typeof issue === 'string') return <span>{issue}</span>;
  return (
    <span>
      {issue.item}
      {issue.urgency && <UrgencyBadge urgency={issue.urgency} />}
    </span>
  );
}

function ChecklistTable({ checklist }) {
  const [checks, setChecks] = useState(() =>
    checklist.map(item => ({ ...item, checked: false }))
  );

  const statusColor = (status) => ({
    '양호': '#28a745', '주의': '#ffc107', '불량': '#dc3545'
  }[status] || '#666');

  return (
    <div style={{
      padding: 'var(--space-md, 1rem)',
      background: '#f8f9fa',
      borderRadius: 'var(--radius-md, 0.5rem)',
      opacity: 0,
      animation: 'fadeInUp 0.4s ease forwards',
      animationDelay: '120ms',
    }}>
      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>📋 점검 체크리스트</strong>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {checks.map((item, i) => (
          <label
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.4rem 0.5rem',
              background: 'white',
              borderRadius: 'var(--radius-sm, 0.25rem)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm, 0.85rem)',
              border: '1px solid var(--color-surface-border, #e9ecef)',
              transition: 'background 0.15s',
            }}
          >
            <input
              type="checkbox"
              checked={item.checked}
              onChange={() => {
                setChecks(prev => prev.map((c, j) =>
                  j === i ? { ...c, checked: !c.checked } : c
                ));
              }}
              style={{ width: '16px', height: '16px', accentColor: 'var(--color-accent)' }}
            />
            <span style={{ flex: 1 }}>{item.item}</span>
            <span style={{
              padding: '0.15rem 0.5rem',
              background: statusColor(item.status) + '18',
              color: statusColor(item.status),
              borderRadius: 'var(--radius-full, 1rem)',
              fontSize: 'var(--font-size-xs, 0.7rem)',
              fontWeight: 600,
            }}>
              {item.status}
            </span>
            {item.note && (
              <span style={{ fontSize: 'var(--font-size-xs, 0.7rem)', color: '#999' }}>
                {item.note}
              </span>
            )}
          </label>
        ))}
      </div>
      <div style={{
        marginTop: '0.5rem',
        fontSize: 'var(--font-size-xs, 0.75rem)',
        color: 'var(--color-text-muted, #999)',
      }}>
        {checks.filter(c => c.checked).length}/{checks.length} 항목 확인 완료
      </div>
    </div>
  );
}

function InspectionResult({ analysis }) {
  const conditionColor = {
    '양호': '#28a745', '주의': '#ffc107', '불량': '#fd7e14', '위험': '#dc3545'
  }[analysis.condition] || '#666';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md, 1rem)' }}>
      {/* Status badges */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        alignItems: 'center',
        opacity: 0,
        animation: 'fadeInUp 0.4s ease forwards',
      }}>
        {analysis.equipment_type && (
          <span style={{
            padding: '0.4rem 1rem',
            background: 'var(--color-surface-dim, #e9ecef)',
            borderRadius: 'var(--radius-full, 1rem)',
            fontSize: 'var(--font-size-sm, 0.85rem)',
            fontWeight: 500,
          }}>
            {analysis.equipment_type}
          </span>
        )}
        {analysis.condition && (
          <span style={{
            padding: '0.5rem 1.2rem',
            background: conditionColor,
            color: 'white',
            borderRadius: 'var(--radius-full, 1rem)',
            fontSize: 'var(--font-size-base, 1rem)',
            fontWeight: 700,
            boxShadow: `0 2px 8px ${conditionColor}44`,
          }}>
            {analysis.condition} {analysis.condition_score ? `(${analysis.condition_score}/10)` : ''}
          </span>
        )}
        {analysis.compliance && (
          <span style={{
            padding: '0.4rem 1rem',
            background: analysis.compliance === '적합' ? 'var(--color-success-bg, #d4edda)' : 'var(--color-danger-bg, #f8d7da)',
            color: analysis.compliance === '적합' ? 'var(--color-success-text, #155724)' : 'var(--color-danger-text, #721c24)',
            borderRadius: 'var(--radius-full, 1rem)',
            fontSize: 'var(--font-size-sm, 0.85rem)',
            fontWeight: 600,
          }}>
            {analysis.compliance}
          </span>
        )}
        {analysis.confidence && <ConfidenceBadge confidence={analysis.confidence} />}
      </div>

      <CollapsibleSection title="상태 설명" icon="📋" content={analysis.description} index={1} />

      {/* 점검 체크리스트 */}
      {analysis.checklist && Array.isArray(analysis.checklist) && analysis.checklist.length > 0 && (
        <ChecklistTable checklist={analysis.checklist} />
      )}

      {/* 발견 문제 (긴급도별) */}
      {analysis.issues_found && analysis.issues_found.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: 'var(--color-warning-bg, #fff3cd)',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '160ms',
        }}>
          <strong>⚠️ 발견된 문제점</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {analysis.issues_found.map((issue, i) => (
              <li key={i} style={{
                marginBottom: '0.35rem',
                fontSize: 'var(--font-size-sm, 0.9rem)',
                lineHeight: 1.5,
              }}>
                <IssueItem issue={issue} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 권장 조치 (긴급도별) */}
      {analysis.recommendations && analysis.recommendations.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: 'var(--color-success-bg, #d4edda)',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '240ms',
        }}>
          <strong>✅ 권장 조치사항</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {analysis.recommendations.map((rec, i) => (
              <li key={i} style={{
                marginBottom: '0.35rem',
                fontSize: 'var(--font-size-sm, 0.9rem)',
                lineHeight: 1.5,
              }}>
                <IssueItem issue={rec} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysis.safety_concerns && (
        <CollapsibleSection title="안전 우려" icon="🚨" content={analysis.safety_concerns} bg="#f8d7da" index={4} />
      )}

      {analysis.next_inspection && (
        <div style={{
          fontSize: 'var(--font-size-sm, 0.9rem)',
          color: 'var(--color-text-secondary, #666)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '400ms',
        }}>
          <strong>다음 점검:</strong> {analysis.next_inspection}
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({ title, icon, content, bg = '#f9f9f9', index = 0 }) {
  const [isOpen, setIsOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  if (!content) return null;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(typeof content === 'string' ? content : JSON.stringify(content));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: silent fail
    }
  };

  return (
    <div style={{
      opacity: 0,
      animation: 'fadeInUp 0.4s ease forwards',
      animationDelay: `${index * 80}ms`,
    }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          padding: '0.25rem 0',
          userSelect: 'none',
        }}
      >
        <strong style={{ fontSize: 'var(--font-size-sm, 0.9rem)' }}>
          {icon} {title}
        </strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={handleCopy}
            title="복사"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8rem',
              color: copied ? 'var(--color-success, #28a745)' : 'var(--color-text-muted, #999)',
              padding: '2px 4px',
              transition: 'color var(--transition-fast, 150ms)',
            }}
          >
            {copied ? '✓' : '📋'}
          </button>
          <span style={{
            fontSize: '0.8rem',
            color: 'var(--color-text-muted, #999)',
            transition: 'transform var(--transition-normal, 250ms)',
            display: 'inline-block',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            ▾
          </span>
        </div>
      </div>
      {isOpen && (
        <div style={{
          padding: '0.75rem',
          background: bg,
          borderRadius: 'var(--radius-md, 0.5rem)',
          marginTop: '0.35rem',
          whiteSpace: 'pre-wrap',
          fontSize: 'var(--font-size-sm, 0.9rem)',
          lineHeight: 1.6,
          animation: 'fadeIn 0.2s ease',
        }}>
          {content}
        </div>
      )}
    </div>
  );
}

function ExerciseResult({ analysis }) {
  const scoreColor = (score) => {
    const s = parseInt(score) || 0;
    if (s >= 8) return '#28a745';
    if (s >= 5) return '#ffc107';
    return '#dc3545';
  };

  const riskColor = { '낮음': '#28a745', '보통': '#ffc107', '높음': '#dc3545' }[analysis.injury_risk] || '#666';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md, 1rem)' }}>
      {/* Status badges */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        alignItems: 'center',
        opacity: 0,
        animation: 'fadeInUp 0.4s ease forwards',
      }}>
        {analysis.exercise_type && (
          <span style={{
            padding: '0.4rem 1rem',
            background: '#e91e6318',
            borderRadius: 'var(--radius-full, 1rem)',
            fontSize: 'var(--font-size-sm, 0.85rem)',
            fontWeight: 600,
            color: '#e91e63',
          }}>
            {analysis.exercise_type}
          </span>
        )}
        {analysis.posture_score && (
          <span style={{
            padding: '0.5rem 1.2rem',
            background: scoreColor(analysis.posture_score),
            color: 'white',
            borderRadius: 'var(--radius-full, 1rem)',
            fontSize: 'var(--font-size-base, 1rem)',
            fontWeight: 700,
          }}>
            자세 점수: {analysis.posture_score}/10
          </span>
        )}
        {analysis.injury_risk && (
          <span style={{
            padding: '0.4rem 1rem',
            background: `${riskColor}18`,
            color: riskColor,
            borderRadius: 'var(--radius-full, 1rem)',
            fontSize: 'var(--font-size-sm, 0.85rem)',
            fontWeight: 600,
            border: `1px solid ${riskColor}33`,
          }}>
            부상위험: {analysis.injury_risk}
          </span>
        )}
        {analysis.confidence && <ConfidenceBadge confidence={analysis.confidence} />}
      </div>

      <CollapsibleSection title="자세 평가" icon="📋" content={analysis.description} index={0} />

      {analysis.good_points && analysis.good_points.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: 'var(--color-success-bg, #d4edda)',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '80ms',
        }}>
          <strong>👍 잘하고 있는 점</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {analysis.good_points.map((p, i) => (
              <li key={i} style={{ marginBottom: '0.25rem', fontSize: 'var(--font-size-sm, 0.9rem)', lineHeight: 1.5 }}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {analysis.issues_found && analysis.issues_found.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: 'var(--color-warning-bg, #fff3cd)',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '160ms',
        }}>
          <strong>⚠️ 자세 문제점</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {analysis.issues_found.map((issue, i) => (
              <li key={i} style={{ marginBottom: '0.25rem', fontSize: 'var(--font-size-sm, 0.9rem)', lineHeight: 1.5 }}>{typeof issue === 'string' ? issue : issue.item || issue}</li>
            ))}
          </ul>
        </div>
      )}

      {analysis.corrections && analysis.corrections.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: '#e3f2fd',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '240ms',
        }}>
          <strong>🔧 교정 방법</strong>
          <ol style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {analysis.corrections.map((c, i) => (
              <li key={i} style={{ marginBottom: '0.35rem', fontSize: 'var(--font-size-sm, 0.9rem)', lineHeight: 1.5 }}>{c}</li>
            ))}
          </ol>
        </div>
      )}

      {analysis.recommendations && analysis.recommendations.length > 0 && (
        <CollapsibleSection title="추가 권장사항" icon="💡" content={analysis.recommendations.join('\n')} bg="#f5f0ff" index={4} />
      )}
    </div>
  );
}

function HealthResult({ analysis }) {
  const statusColor = { '정상': '#28a745', '경계': '#ffc107', '이상': '#dc3545' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md, 1rem)' }}>
      {/* 면책 고지 */}
      <div style={{
        padding: 'var(--space-sm, 0.5rem) var(--space-md, 1rem)',
        background: '#fff3cd',
        borderRadius: 'var(--radius-md, 0.5rem)',
        fontSize: 'var(--font-size-xs, 0.75rem)',
        color: '#856404',
        border: '1px solid #ffc10733',
        opacity: 0,
        animation: 'fadeInUp 0.4s ease forwards',
      }}>
        ⚠️ AI 참고용 분석입니다. 정확한 진단은 반드시 의사에게 문의하세요.
      </div>

      {/* Status badges */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        alignItems: 'center',
        opacity: 0,
        animation: 'fadeInUp 0.4s ease 0.05s forwards',
      }}>
        {analysis.checkup_type && (
          <span style={{
            padding: '0.4rem 1rem',
            background: '#00bcd418',
            borderRadius: 'var(--radius-full, 1rem)',
            fontSize: 'var(--font-size-sm, 0.85rem)',
            fontWeight: 600,
            color: '#00bcd4',
          }}>
            {analysis.checkup_type}
          </span>
        )}
        {analysis.overall_status && (
          <span style={{
            padding: '0.5rem 1.2rem',
            background: statusColor[analysis.overall_status] || '#666',
            color: 'white',
            borderRadius: 'var(--radius-full, 1rem)',
            fontSize: 'var(--font-size-base, 1rem)',
            fontWeight: 700,
          }}>
            종합: {analysis.overall_status}
          </span>
        )}
        {analysis.confidence && <ConfidenceBadge confidence={analysis.confidence} />}
      </div>

      <CollapsibleSection title="건강 상태 요약" icon="📋" content={analysis.description} index={0} />

      {/* 이상 항목 */}
      {analysis.abnormal_items && analysis.abnormal_items.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: '#f8d7da',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '120ms',
        }}>
          <strong>🔴 이상/경계 항목</strong>
          <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {analysis.abnormal_items.map((item, i) => (
              <div key={i} style={{
                padding: '0.5rem 0.75rem',
                background: 'white',
                borderRadius: 'var(--radius-sm, 0.25rem)',
                fontSize: 'var(--font-size-sm, 0.85rem)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <strong>{item.item}</strong>
                  <span style={{
                    padding: '0.1rem 0.5rem',
                    background: (statusColor[item.status] || '#666') + '18',
                    color: statusColor[item.status] || '#666',
                    borderRadius: 'var(--radius-full, 1rem)',
                    fontSize: 'var(--font-size-xs, 0.7rem)',
                    fontWeight: 600,
                  }}>
                    {item.status}
                  </span>
                </div>
                <div style={{ color: '#666', fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                  수치: {item.value} (참고: {item.reference})
                </div>
                {item.explanation && (
                  <div style={{ marginTop: '0.25rem', lineHeight: 1.4 }}>{item.explanation}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 정상 항목 */}
      {analysis.normal_items && analysis.normal_items.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: 'var(--color-success-bg, #d4edda)',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '160ms',
        }}>
          <strong>🟢 정상 항목</strong>
          <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {analysis.normal_items.map((item, i) => (
              <div key={i} style={{
                padding: '0.35rem 0.6rem',
                background: 'white',
                borderRadius: 'var(--radius-sm, 0.25rem)',
                fontSize: 'var(--font-size-sm, 0.85rem)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span>{item.item}</span>
                <span style={{ color: '#28a745', fontSize: 'var(--font-size-xs, 0.75rem)', fontWeight: 600 }}>
                  {item.value ? `${item.value} ✓` : '정상'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 생활습관 조언 */}
      {analysis.lifestyle_advice && analysis.lifestyle_advice.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: 'var(--color-success-bg, #d4edda)',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '200ms',
        }}>
          <strong>💡 생활 습관 조언</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {analysis.lifestyle_advice.map((a, i) => (
              <li key={i} style={{ marginBottom: '0.25rem', fontSize: 'var(--font-size-sm, 0.9rem)', lineHeight: 1.5 }}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 추가 검사 필요 */}
      {analysis.follow_up && analysis.follow_up.length > 0 && (
        <div style={{
          padding: 'var(--space-md, 1rem)',
          background: '#e3f2fd',
          borderRadius: 'var(--radius-md, 0.5rem)',
          opacity: 0,
          animation: 'fadeInUp 0.4s ease forwards',
          animationDelay: '280ms',
        }}>
          <strong>🏥 추가 검사 권장</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {analysis.follow_up.map((f, i) => (
              <li key={i} style={{ marginBottom: '0.25rem', fontSize: 'var(--font-size-sm, 0.9rem)', lineHeight: 1.5 }}>{f}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
